// Hub-answer identity that outlives the run. The marker rides on the run's
// state, so a remounted or recovered chat can't re-derive it from the stream;
// it's carried on the message instead, in the shape panda-service persists
// (`knowledgeHub: { source, offerId }`), which its thread read passes through.

import type { Message } from '@ag-ui/client';
import { AG_UI_ROLE, isHubSource } from '../agent/ag-ui.constants';
import type { HubSource } from '../agent/ag-ui.constants';

/** The persisted hub marker on an assistant message — panda-service's `knowledgeHub` envelope. */
export interface HubTaggedMessage {
  knowledgeHub?: { source?: string; offerId?: string };
}

/** Which messages are hub answers, and which message each offer's run settled on. */
export interface HubAnswers {
  sources: Map<string, HubSource>;
  offerAnswers: Map<string, string>;
}

export function createHubAnswers(): HubAnswers {
  return { sources: new Map(), offerAnswers: new Map() };
}

/** The hub source a persisted message carries, or undefined when it isn't a hub answer. */
export function persistedHubSource(message: Message): HubSource | undefined {
  const source = (message as Message & HubTaggedMessage).knowledgeHub?.source;
  return isHubSource(source) ? source : undefined;
}

/**
 * One hub answer per run: tag `messageId` for `offerId`, untagging whatever the
 * same offer tagged before. A miss streams an empty tool-call shell and then the
 * refusal text — only the latest of a run's messages is its answer.
 */
export function tagHubAnswer(
  answers: HubAnswers,
  { offerId, messageId, source }: { offerId?: string; messageId: string; source: HubSource },
): void {
  if (offerId) {
    const previous = answers.offerAnswers.get(offerId);
    if (previous && previous !== messageId) {
      answers.sources.delete(previous);
    }
    answers.offerAnswers.set(offerId, messageId);
  }
  answers.sources.set(messageId, source);
}

/**
 * Seed from persisted messages. panda-service stamps every message of a hub
 * run, so the same one-answer-per-run rule applies: the latest assistant
 * message an offer stamped wins.
 */
export function seedHubAnswers(answers: HubAnswers, messages: Message[]): void {
  for (const message of messages) {
    if (message.role !== AG_UI_ROLE.ASSISTANT) continue;
    const source = persistedHubSource(message);
    if (!source) continue;
    const offerId = (message as Message & HubTaggedMessage).knowledgeHub?.offerId;
    tagHubAnswer(answers, { offerId, messageId: message.id, source });
  }
}

export function hubAnswersFrom(messages: Message[]): HubAnswers {
  const answers = createHubAnswers();
  seedHubAnswers(answers, messages);
  return answers;
}

/**
 * Stamp live hub tags onto messages as the persisted marker. Returns new
 * objects — the agent's messages are never mutated (ag-ui freezes the copies it
 * hands to subscribers).
 */
export function withHubSources(messages: Message[], sources: Map<string, HubSource>): Message[] {
  return messages.map(message => {
    const source = sources.get(message.id);
    if (!source) return message;
    const tagged = message as Message & HubTaggedMessage;
    const stamped: Message & HubTaggedMessage = {
      ...message,
      knowledgeHub: { ...tagged.knowledgeHub, source },
    };
    return stamped;
  });
}
